import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BranchMetrics {
  teaching_hours: number;
  salary: number;
  sudden_leave_hours: number;
}

export interface PeriodSnapshotData {
  total_salary: number;
  total_teaching_hours: number;
  total_sudden_leave_hours: number;
  branches: Record<string, BranchMetrics>;
  row_count: number;
}

export interface PeriodSnapshotResponse {
  success: boolean;
  data: PeriodSnapshotData;
  start_date: string;
  end_date: string;
}

/**
 * Calculate default period dates based on payroll schedule:
 * Period 1: 26th prev month → 9th current month
 * Period 2: 10th current month → 25th current month
 */
export function getDefaultPeriodDates(): { startDate: string; endDate: string } {
  const now = new Date();
  const day = now.getDate();
  const month = now.getMonth(); // 0-indexed
  const year = now.getFullYear();

  if (day <= 9) {
    // Period 1: 26th of prev month to 9th of current month
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    return {
      startDate: `${prevYear}-${String(prevMonth + 1).padStart(2, "0")}-26`,
      endDate: `${year}-${String(month + 1).padStart(2, "0")}-09`,
    };
  } else if (day <= 25) {
    // Period 2: 10th to 25th of current month
    return {
      startDate: `${year}-${String(month + 1).padStart(2, "0")}-10`,
      endDate: `${year}-${String(month + 1).padStart(2, "0")}-25`,
    };
  } else {
    // Day >= 26: Period 1 of next month (26th current → 9th next)
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    return {
      startDate: `${year}-${String(month + 1).padStart(2, "0")}-26`,
      endDate: `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-09`,
    };
  }
}

export function usePeriodSnapshot(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ["period-snapshot", startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-period-snapshot", {
        body: { start_date: startDate, end_date: endDate },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Unknown error");

      return data as PeriodSnapshotResponse;
    },
    enabled: !!startDate && !!endDate,
  });
}
